import {
	ActionIcon,
	Box,
	Button,
	createStyles,
	Divider,
	Flex,
	Icon,
	Stack,
	TFCard,
	TFHTMLText,
	TFHTMLTextEditor,
	TFNotifier,
	TFText,
	Tooltip,
} from '@tf/ui';
import { fmt, saveFile } from '@tf/utils';
import React, { useState } from 'react';

import {
	useRevokeCaseRequestMutation,
	useSendCaseRequestMutation,
	useUpdateCaseRequestTextMutation,
} from '@/core/api/caseRequests';
import { downloadFile, isPreviewableFile } from '@/core/api/files';
import type { CaseRequest as CaseRequestType, CaseStatus, CaseWithData } from '@/core/types/cases';

import { DueDate } from './DueDate';

export const CaseRequest: React.FC<{
	caseRequest: CaseRequestType;
	caseData: CaseWithData;
	livenessCheckResults?: React.ReactNode;
	paymentObjectId?: string;
}> = ({ caseRequest, caseData, livenessCheckResults, paymentObjectId }) => {
	const isEditable = caseData.labels.status === 'ACTIVE' && !caseRequest.labels.sent_at;

	return isEditable ? (
		<CaseRequestEditor caseRequest={caseRequest} />
	) : (
		<CaseRequestView
			caseId={caseData.id}
			caseStatus={caseData.labels.status}
			caseRequest={caseRequest}
			livenessCheckResults={livenessCheckResults}
			paymentObjectId={paymentObjectId}
		/>
	);
};

const CaseRequestEditor: React.FC<{ caseRequest: CaseRequestType }> = ({ caseRequest }) => {
	const [editorContent, setEditorContent] = useState(caseRequest.data.request_text);

	const updateCaseRequestTextMutation = useUpdateCaseRequestTextMutation();
	const sendCaseRequestMutation = useSendCaseRequestMutation();

	return (
		<CaseRequestLayout
			toolbar={<DueDate caseRequestId={caseRequest.id} date={caseRequest.labels.due_date} isEditable />}
			actions={
				<>
					<Button
						variant="light"
						size="sm"
						onClick={() => {
							updateCaseRequestTextMutation.mutate({
								caseRequestId: caseRequest.id,
								text: editorContent,
							});
						}}
					>
						Save changes
					</Button>

					<Button
						size="sm"
						onClick={() => {
							// Always save a request text before sending it
							updateCaseRequestTextMutation.mutate(
								{
									caseRequestId: caseRequest.id,
									text: editorContent,
								},
								{
									onSuccess: () => {
										sendCaseRequestMutation.mutate({ caseRequestId: caseRequest.id });
									},
								},
							);
						}}
					>
						Send request
					</Button>
				</>
			}
			content={
				<Stack gap="1.5rem">
					<Stack gap="0.5rem">
						<TFText size="md" fw="600">
							Request message:
						</TFText>
						<TFHTMLTextEditor content={editorContent} onChange={setEditorContent} />
					</Stack>

					{caseRequest.data.action ? (
						<Stack gap="0.5rem">
							<TFText size="md" fw="600">
								Link:
							</TFText>
							{caseRequest.data.action.label}: {caseRequest.data.action.url}
						</Stack>
					) : null}
				</Stack>
			}
		/>
	);
};

const CaseRequestView: React.FC<{
	caseId: string;
	caseStatus: CaseStatus;
	caseRequest: CaseRequestType;
	livenessCheckResults?: React.ReactNode;
	paymentObjectId?: string;
}> = ({ caseId, caseStatus, caseRequest, livenessCheckResults, paymentObjectId }) => {
	const revokeCaseRequestMutation = useRevokeCaseRequestMutation();

	const isRevocable =
		!!caseRequest.labels.sent_at &&
		(caseRequest.labels.status === 'DRAFT' ||
			caseRequest.labels.status === 'SENT' ||
			caseRequest.labels.status === 'VIEWED') &&
		caseStatus === 'ACTIVE';

	return (
		<CaseRequestLayout
			toolbar={<DueDate caseRequestId={caseRequest.id} date={caseRequest.labels.due_date} isEditable={false} />}
			actions={
				isRevocable ? (
					<Button
						size="sm"
						onClick={() => {
							revokeCaseRequestMutation.mutate({
								caseId,
								caseRequestId: caseRequest.id,
								paymentObjectId,
							});
						}}
					>
						Revoke request
					</Button>
				) : null
			}
			content={
				<>
					<MessageContainer
						label="Request message"
						text={caseRequest.data.request_text}
						date={caseRequest.labels.sent_at}
					/>
					{caseRequest.data.action ? (
						<Stack gap="0.5rem" mt="1.5rem">
							<TFText size="md" fw="600">
								Link:
							</TFText>
							{caseRequest.data.action.label}: {caseRequest.data.action.url}
						</Stack>
					) : null}
					{caseRequest.labels.status === 'REVOKED' ? (
						<>
							<Divider
								sx={({ colors }) => ({
									borderColor: colors.gray[3],
									borderWidth: '0.5px',
								})}
								my="sm"
							/>
							<TFText size="md" fw="600">
								Request has been revoked
							</TFText>
						</>
					) : caseRequest.labels.status === 'UPDATED' ? (
						<>
							<Divider
								sx={({ colors }) => ({
									borderColor: colors.gray[3],
									borderWidth: '0.5px',
								})}
								my="sm"
							/>
							<Stack gap="1rem">
								<MessageContainer
									label="Response message"
									text={caseRequest.data.response_text ?? ''}
									date={caseRequest.meta.responded_at}
								/>
								{/* Files */}
								<Stack gap="0.5rem">
									{caseRequest.response_files.map((file) => {
										const canPreview = isPreviewableFile(file.object.meta['@mime']);

										return (
											<Flex key={file.object.id} gap={8} align="center">
												<Button
													variant="light"
													size="sm"
													flex={1}
													onClick={async () => {
														const blob = await downloadFile(file.object.id);
														saveFile(blob, file.object.meta['@filename']);
													}}
													rightSection={<Icon.IconDownload size="18" />}
												>
													{file.object.meta['@filename']}
												</Button>

												{canPreview && (
													<CasePreviewButton fileId={file.object.id} fileName={file.object.meta['@filename']} />
												)}
											</Flex>
										);
									})}
								</Stack>
								{livenessCheckResults}
							</Stack>
						</>
					) : null}
				</>
			}
		/>
	);
};

const CaseRequestLayout: React.FC<{
	toolbar: React.ReactNode;
	actions: React.ReactNode;
	content: React.ReactNode;
}> = ({ toolbar, actions, content }) => {
	return (
		<TFCard innerPadding="1rem">
			<Stack gap="1rem">
				<Flex align="center" justify="space-between">
					<Box>{toolbar}</Box>
					<Flex gap="0.5rem">{actions}</Flex>
				</Flex>
				<Box>{content}</Box>
			</Stack>
		</TFCard>
	);
};

const useMessageContainerStyles = createStyles(() => {
	return {
		date: {
			color: '#64748B',
			fontWeight: 400,
		},
		markdownContainer: {
			flexGrow: 1,
		},
		markdown: {
			'& > :last-of-type': {
				marginBottom: 0,
			},
		},
	};
});

const MessageContainer = ({ label, text, date }: { label: string; text: string; date?: string }) => {
	const { classes } = useMessageContainerStyles();

	return (
		<Stack gap="0.5rem">
			<Flex align="baseline" gap="1rem">
				<TFText size="md" fw="600">
					{label}
				</TFText>
				<TFText className={classes.date}>
					{date ? `Sent ${fmt.toDate(date, { preset: 'full_date_with_time' })}` : 'Not sent'}
				</TFText>
			</Flex>
			<TFHTMLText className={classes.markdown} text={text} />
		</Stack>
	);
};

const CasePreviewButton: React.FC<{ fileId: string; fileName: string }> = ({ fileId, fileName }) => {
	const handlePreview = React.useCallback(async () => {
		try {
			const blob = await downloadFile(fileId);
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			URL.revokeObjectURL(url);
		} catch (e) {
			console.error('Preview failed:', e);
			TFNotifier.error('File preview failed', {
				id: 'CasePreviewError',
			});
		}
	}, [fileId]);

	return (
		<Tooltip label={`Preview ${fileName}`} withArrow>
			<ActionIcon variant="subtle" color="blue" onClick={handlePreview}>
				<Icon.IconEye size={18} stroke={1.5} />
			</ActionIcon>
		</Tooltip>
	);
};
